import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { DurationModel } from '../../models/primitive/duration.model';
import { ConfigService } from '../../services/config/config.service';
import { DurationManualInputComponent } from './duration-manual-input/duration-manual-input.component';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';

@Component({
    selector: 'pace-core-duration-stepper',
    templateUrl: './duration-stepper.component.html',
    styleUrls: ['./duration-stepper.component.scss'],
})
export class DurationStepperComponent implements OnInit, OnChanges, OnDestroy {
    private previousValue: moment.Duration | undefined;

    @Input()
    public model: DurationModel;

    @Output()
    public modelChange = new EventEmitter<DurationModel>();

    @Input()
    public parentForm: FormArray;

    @Input()
    public ariaLabel = 'Duration Stepper Input';

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<DurationModel>>();

    public strings: {
        timePickerDone: string;
        timePickerCancel: string;
    };

    public durationStepperControl: FormControl;
    public useCustomDurationStepper = false;

    private isInputManuallyUpdated = false;

    private destroySubject = new Subject<void>();
    public constructor(
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly configService: ConfigService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.strings = {
            timePickerDone: <string>this.translate.instant('DURATION_STEPPER_DONE'),
            timePickerCancel: <string>this.translate.instant('DURATION_STEPPER_CANCEL'),
        };
        this.useCustomDurationStepper = this.configService.getUseCustomDurationStepper();
    }

    public ngOnInit(): void {
        this.previousValue = this.model.value ? this.model.value.clone() : undefined;

        const visibleValue = this.updateValue(this.model.value);

        const durationStepperValidator = (control: AbstractControl): ValidationErrors | null =>
            this.checkValidity(control);
        this.durationStepperControl = this.formBuilder.control(visibleValue, durationStepperValidator);

        this.durationStepperControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap(() => {
                    // if the value has changed externally
                    // then we don't need to emit model change event
                    if (this.durationStepperControl.dirty || this.isInputManuallyUpdated) {
                        this.modelChange.emit(this.model);
                        this.valueChange.emit({
                            oldValue: this.previousValue,
                            newValue: this.model.value,
                        });
                        this.isInputManuallyUpdated = false;
                    }
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.durationStepperControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in DurationStepperComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    private processExternalChange(): void {
        // make sure that the control value always matches the model value, even if it was changed externally
        if (
            typeof this.model.value !== 'undefined' &&
            typeof this.previousValue !== 'undefined' &&
            this.previousValue.asMilliseconds() !== this.model.value.asMilliseconds()
        ) {
            this.previousValue = this.model.value.clone();
            const visibleValue = this.updateValue(this.model.value);
            this.durationStepperControl.setValue(visibleValue);
        }
        if (this.model.options.readonly && this.durationStepperControl.enabled) {
            this.durationStepperControl.disable({ onlySelf: true, emitEvent: false });
        }
        if (!this.model.options.readonly && this.durationStepperControl.disabled) {
            this.durationStepperControl.enable({ onlySelf: true, emitEvent: false });
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model && this.durationStepperControl) {
            this.model = <DurationModel>changesObj.model.currentValue;
            const visibleValue = this.updateValue(this.model.value);
            this.durationStepperControl.setValue(visibleValue);
            this.processExternalChange();
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.durationStepperControl);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public increase(): void {
        const oldValue = this.model.value ? this.model.value.clone() : undefined;
        if (this.model.inc()) {
            this.processExternalChange();
            this.previousValue = oldValue;
            const visibleValue = this.updateValue(this.model.value);
            this.isInputManuallyUpdated = true;
            this.durationStepperControl.setValue(visibleValue);
        }
    }

    public decrease(): void {
        const oldValue = this.model.value ? this.model.value.clone() : undefined;
        if (this.model.dec()) {
            this.processExternalChange();
            this.previousValue = oldValue;
            const visibleValue = this.updateValue(this.model.value);
            this.isInputManuallyUpdated = true;
            this.durationStepperControl.setValue(visibleValue);
        }
    }

    public onClick(): void {
        const modalInstance = this.modalService.show(DurationManualInputComponent, {
            backdrop: 'static',
            class: 'modal-sm',
            initialState: {
                model: this.model,
            },
            ariaLabelledBy: 'duration-manual-input-modal-title',
        });

        modalInstance.content?.modelChanged.subscribe((duration: DurationModel) => {
            this.processExternalChange();
            this.isInputManuallyUpdated = true;
            this.previousValue = this.model.value?.clone();
            this.model.value = duration.value;
            const visibleValue = this.updateValue(this.model.value);
            this.durationStepperControl.setValue(visibleValue);
        });
    }

    private updateValue(duration: moment.Duration | undefined): string {
        let visibleValue = '';
        if (duration) {
            this.model.set(duration);
            visibleValue = duration.format('HH:mm', { trim: false });
        }

        return visibleValue;
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};

        const controlValue = <string>control.value;
        const potentialValue = moment.duration(controlValue);
        const formatPattern = this.clientManager.getTimeFormat();

        if (this.model.options.required && !control.value) {
            errors.required = 'This input is mandatory';
        }
        if (potentialValue.asMilliseconds() > this.model.options.maxValue.asMilliseconds()) {
            errors.max = {
                message: 'Max value exceeded',
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')} ${this.translate.instant(
                    'TIME_INPUT_VALID_TIME_RANGE_UPTO_TEXT',
                    {
                        maxTime: this.model.options.maxValue.format(formatPattern, { trim: false }),
                    },
                )}`,
            };
        }
        if (potentialValue.asMilliseconds() < this.model.options.minValue.asMilliseconds()) {
            errors.min = {
                min: 'Min value exceeded',
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')} ${this.translate.instant(
                    'TIME_INPUT_VALID_TIME_RANGE_FROM_TEXT',
                    {
                        minTime: this.model.options.minValue.format(formatPattern, { trim: false }),
                    },
                )}`,
            };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
